import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as boardsStore from '../extensions/bundled/task-manager/boardsStore';
import { ownTaskCount as countOwnTasks } from './boards/ownTaskCount';
// MCP subscribers learn the board list moved (create / edit / delete / share).
// Deliberately NOT bumpBoards(): bumping boardsVersion re-reads the open board,
// which this tab defers during a drag (pendingBoardsBumpRef) — a notify that
// cannot disturb the editor is the safer signal. Not in boardsStore either:
// MCP's own writes go through it and must not notify themselves (S6).
import { useTaskBoardUiStore } from '../store/taskBoardUiStore';
import { useTeamsStore } from '../store/teamsStore';
import { canShareWithTeam } from '../utils/workspaceShareEligibility';
import teamsService from '../services/teamsService';
import { useAuthStore } from '../store/authStore';
import { useTeamPresenceStore, selectBoardViewers } from '../store/teamPresenceStore';
import { pushTeamPresenceContext } from '../services/teamChannelService';
import { PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useTabsStore, selectCurrentTaskManagerTab } from '../store/tabsStore';
import { useBoardsUiStore } from '../extensions/bundled/task-manager/boardsUiStore';
import { roleForTeam as roleOfTeam, isViewerRole } from '../extensions/bundled/task-manager/boardRoles';
import { dragLocked, taskMatchesFilters } from '../extensions/bundled/task-manager/boardFilters';
import { paletteOf } from '../extensions/bundled/task-manager/boardLabels';
import useGroupTasks from './boards/useGroupTasks';
import useMoveStableTasks from './boards/useMoveStableTasks';
import useBoardPersistence from './boards/useBoardPersistence';
import useBoardAdmin from './boards/useBoardAdmin';
import useBoardTaskActions from './boards/useBoardTaskActions';
import useBoardDrag from './boards/useBoardDrag';
import useBoardCreatedByFill from './boards/useBoardCreatedByFill';
import { LOCATION } from '../records/itemLocation';
import { useCloudReadOnly } from '../plans/useCloudReadOnly';
import './boards/tokens.css';
import './TaskBoardTab.css';
import { sortByPosition } from './boards/taskBoardTabConstants';
import { renderTaskBoardTab } from './boards/renderTaskBoardTab';
import { useBoardDiskSync } from './boards/useBoardDiskSync';
import useBoardTaskKeysFill from './boards/useBoardTaskKeysFill';
import useCommitAutoClose from './boards/useCommitAutoClose';
import { useSettledLayout } from '../hooks/useSettledLayout';
import { TaskKeyAliasesContext, TaskKeysContext } from './boards/TaskKeys';
import { prefixAliasesOf, prefixesTakenByOthers } from '../extensions/bundled/task-manager/taskKeyPrefixEdit';
import { taskKeysFor } from '../extensions/bundled/task-manager/taskNumbers';

/**
 * Task Manager's tab-content view. One instance, rendered by EditorPane
 * whenever `activeTabType === 'task-manager-board'` — like DbManagerResultsTab,
 * it reads which tab is active (`selectCurrentTaskManagerTab`) and branches:
 * the pinned "Boards" tab (`tab.boardId` null) shows the boards grid, any
 * other tab shows that board's Kanban view. Board tiles/creation open a new
 * tab via `openTaskManagerBoardTab` instead of local view-state toggling.
 * Reads/writes board files directly via Tauri invoke() (task_board_* Rust
 * commands), bypassing the guest extension sandbox — see manifest.js/entry.js.
 */
export default function TaskBoardTab({ isActive = true }) {
  const tab = useTabsStore(selectCurrentTaskManagerTab);
  const [boards, setBoards] = useState([]);
  const [boardsLoading, setBoardsLoading] = useState(true);
  // Filter value lives in the shared store — the input renders above the grid
  // (see the boards-home block below); the store keeps Ctrl/Cmd+F and the
  // titlebar slot working against one value.
  const boardFilter = useTaskBoardUiStore((s) => s.filter);
  const setBoardFilter = useTaskBoardUiStore((s) => s.setFilter);
  // Teams drive: the create-modal "Team" option + picker, team-board badges, and
  // viewer read-only gating. `teamRoleById` maps teamId -> the user's role.
  const teams = useTeamsStore((s) => s.teams);
  const shareableTeams = useMemo(() => (teams || []).filter(canShareWithTeam), [teams]);
  const teamById = useMemo(() => {
    const m = new Map();
    for (const t of teams || []) if (t?.id != null) m.set(String(t.id), t);
    return m;
  }, [teams]);
  // The role lookup itself moved to `boardRoles.js` (pure, teams-array in) so
  // MCP's board writes can ask the SAME question: a viewer's push is rejected
  // by the server and a rejected push jams that scope's outbox permanently, so
  // a writer without a UI needs this exact mirror, not a second one.
  const roleForTeam = useCallback((teamId) => roleOfTeam(teams, teamId), [teams]);
  const isViewerForTeam = useCallback((teamId) => isViewerRole(teams, teamId), [teams]);
  const [board, setBoard] = useState(null);
  const [boardLoading, setBoardLoading] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [deleteGroupTarget, setDeleteGroupTarget] = useState(null);
  const [boardModal, setBoardModal] = useState(null); // { mode, id?, name, description, color, visibility, teamId }

  const [shareBoardTarget, setShareBoardTarget] = useState(null); // { id, name, teamId } for the share-with-team modal
  const [privateBoardTarget, setPrivateBoardTarget] = useState(null); // { id, name, teamId } for "Make private"
  const [deleteBoardTarget, setDeleteBoardTarget] = useState(null);
  // Viewers on a team board get a read-only board (server also rejects their
  // pushes). readOnlyRef mirrors it for the eager updateBoard guard.
  // Plans v2 card 3.4: so is a CLOUD board of a plan without cloud sync — it
  // can be downloaded to this device (tile menu) and edited there.
  const cloudReadOnly = useCloudReadOnly();
  const boardCloudReadOnly = !!(board && board.location === LOCATION.CLOUD && board.visibility !== 'team' && cloudReadOnly);
  const boardReadOnly = !!(board && board.visibility === 'team' && isViewerForTeam(board.teamId)) || boardCloudReadOnly;
  const readOnlyRef = useRef(false);
  useEffect(() => {
    readOnlyRef.current = boardReadOnly;
  }, [boardReadOnly]);

  // Presence: tell the team channel which board is open so teammates viewing
  // the same board can see each other's avatar. No-op for private boards.
  const currentUserId = useAuthStore((s) => s.user?.id);
  const teamPresenceByTeam = useTeamPresenceStore((s) => s.byTeam);
  const boardViewers = useMemo(
    () =>
      board?.visibility === 'team'
        ? selectBoardViewers(teamPresenceByTeam, board.teamId, board.id, currentUserId)
        : [],
    [teamPresenceByTeam, board?.visibility, board?.teamId, board?.id, currentUserId]
  );
  useEffect(() => {
    // This tab is kept mounted-but-hidden when navigating away from Boards
    // (see EditorPane), so `isActive` — not just board identity — must gate
    // the context: otherwise leaving Boards entirely never clears "viewing
    // this board" server-side, leaving stale presence for other viewers.
    //
    // `board` itself is deliberately kept in memory after closing a board
    // (see the boardId load effect below) so returning to it reconciles in
    // place instead of remounting dnd-kit — which means `board` staying set
    // does NOT mean the board is still open. `boardId` (null when the
    // boards-home grid is showing, i.e. `showGrid`) is the real signal.
    if (!isActive || !tab?.boardId || !board || board.visibility !== 'team' || board.teamId == null) {
      return undefined;
    }
    pushTeamPresenceContext(board.teamId, board.id);
    return () => pushTeamPresenceContext(board.teamId, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the fields above are the whole signal; `board` gets a new identity on every card edit, which would re-announce presence on each keystroke
  }, [tab?.boardId, board?.id, board?.teamId, board?.visibility, isActive]);

  const [activeDragId, setActiveDragId] = useState(null);
  // Undo/redo: two bounded stacks of { before, after } steps (see updateBoard).
  // canUndo/canRedo drive the toolbar buttons' disabled state.
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const saveTimeoutRef = useRef(null);
  // Wall-clock timestamp of the first scheduleDebounce() call since the timer
  // last actually fired/flushed — lets a continuous call burst (e.g. typing a
  // description) still force a persist at a bounded interval instead of being
  // pushed out indefinitely by the trailing debounce. Team boards only.
  const burstStartRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const loadedBoardIdRef = useRef(null);
  const boardIdRef = useRef(null); // mirrors boardId for async callbacks (sync effect)
  // True while a dnd-kit drag is in flight (set at drag start, cleared at drag
  // end/cancel). A sync re-read landing mid-gesture would yank the cards out
  // from under the pointer and desync dnd-kit's measurements, so the
  // boardsVersion effect defers instead (pendingBoardsBumpRef) and the drop
  // handler replays the reconcile once the gesture is over.
  const dragActiveRef = useRef(false);
  const pendingBoardsBumpRef = useRef(false);
  // A pull may have put records on disk that the dirty in-memory boards lack;
  // persist() holds its whole-scope writes until reconcileFromDisk has rebased
  // them (writing now would tombstone the pulled records — card 4f6be6c3).
  const pullPendingRef = useRef(false);
  const reconcileGenRef = useRef(0); // only the newest reconcile clears it
  // Records published while a drag was in flight, replayed at drag end BEFORE
  // the deferred reconcile — see the subscribeBoardChanges effect below.
  const pendingBoardChangesRef = useRef([]);
  const applyBoardRecordsRef = useRef(null);
  // Latest reconcileFromDisk(), so drag handlers can replay a deferred sync
  // re-read without depending on the callback in their deps.
  const reconcileFromDiskRef = useRef(null);
  // False until the boards grid has loaded once; later refreshes (sync echoes,
  // tab revisits) swap data in place without flashing the loading state.
  const hasLoadedBoardsRef = useRef(false);
  // Ids of tasks created just now via "Add Task". A card/row auto-enters title
  // edit mode only if its id is in here (then removes itself), so re-mounting an
  // existing title-less task on tab switch never steals focus.
  const justCreatedTaskIdsRef = useRef(new Set());
  // Ids of lists created just now via "Add list". A Column/ListSection enters
  // rename mode on mount only if its id is in here (then removes itself).
  const justCreatedGroupIdsRef = useRef(new Set());
  // boardId -> latest in-memory board. This is the authoritative source for
  // local state: switching back to an already-opened board renders it instantly
  // (no disk-read spinner, no remount flash) and is NOT re-read from disk, so a
  // stale disk copy can never clobber newer local edits. Cross-device changes
  // invalidate entries here (see the boardsVersion effect).
  const boardCacheRef = useRef(new Map());
  // Per-board dirty tracking: id -> the exact board version that still needs to
  // be written to disk. Persistence is per-board (not a single global rev), so a
  // write that fails stays queued against the *correct* board and is retried,
  // instead of being orphaned when you switch away to another board.
  const dirtyRef = useRef(new Map());
  const savingRef = useRef(new Set()); // ids with a write in flight (no overlap)
  const deletedIdsRef = useRef(new Set()); // ids deleted this session — never (re)write them
  // boardId -> Set<entity id> for entities with an unsaved local edit still
  // pending this board's debounced persist flush (task/group ids, or the
  // "__meta__" sentinel for a board-metadata edit). Finer-grained than
  // dirtyRef (which just marks "this whole board has something unsaved" for
  // the persist queue/disk-reconcile skip) — this is ONLY consulted by the
  // WS realtime fast path below, so a live edit to one card doesn't block
  // realtime updates to every other card on the board (see subscribeBoardChanges).
  // Cleared for a board id once persist() successfully flushes that board.
  const dirtyEntityIdsRef = useRef(new Map());
  const markDirtyEntity = useCallback((boardIdForEntity, ids) => {
    if (!boardIdForEntity) return;
    let set = dirtyEntityIdsRef.current.get(boardIdForEntity);
    if (!set) {
      set = new Set();
      dirtyEntityIdsRef.current.set(boardIdForEntity, set);
    }
    for (const id of Array.isArray(ids) ? ids : [ids]) {
      if (id) set.add(id);
    }
  }, []);
  // Reactive mirror of "does the open board have anything unsaved" — pure UI
  // (the TaskDetailModal "Saved"/"Saving…" pill); dirtyRef itself stays the
  // source of truth for persistence.
  const [saveState, setSaveState] = useState('saved');
  const recomputeSaveState = useCallback(() => {
    const id = boardIdRef.current;
    setSaveState(id && dirtyRef.current.has(id) ? 'saving' : 'saved');
  }, []);
  const persistRef = useRef(null); // holds latest persist() to break a cycle
  const aliveRef = useRef(true); // false after unmount: stop scheduling retries
  // Current active board, mirrored synchronously at every setBoard call site so
  // flushSave/updateBoard always read the live board without waiting for paint.
  const boardRef = useRef(null);
  // Undo/redo history: arrays of { before, after } steps for the OPEN board, newest
  // last. Snapshots are the immutable board objects updateBoard already produces
  // (all edits are copy-on-write), so no deep clone is needed. Both stacks are
  // capped at MAX_HISTORY and reset only on board switch/close — never on a
  // disk/sync re-read (see the NOTE in reconcileFromDisk) — so memory stays
  // bounded without losing the user's undo history to sync echoes.
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  // Board captured at drag start; committed as a single undo entry at drag end so
  // a drag's many per-frame updateBoard calls collapse into one undoable step.
  const dragUndoSnapshotRef = useRef(null);

  // Recompute the undo/redo button flags from the current stack depths. Cheap;
  // called after any push/pop so setState only fires when a flag actually flips.
  const syncHistoryFlags = useCallback(() => {
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(redoStackRef.current.length > 0);
  }, []);

  // Clear all history for the open board. Used on board switch and when a disk/
  // sync re-read swaps the board out from under us (stale snapshots must not be
  // replayed onto a board they no longer describe). Defined up here so the
  // board-switch effect below can depend on it without a temporal-dead-zone.
  const resetHistory = useCallback(() => {
    undoStackRef.current = [];
    redoStackRef.current = [];
    dragUndoSnapshotRef.current = null;
    syncHistoryFlags();
  }, [syncHistoryFlags]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  // Personal-sync nonce: bumped when a change is pulled from another device.
  const boardsVersion = useBoardsUiStore((s) => s.boardsVersion);

  const loadBoards = useCallback(() => {
    // Only the first load shows the loading state; refreshes (sync echoes,
    // returning to the grid) swap the tiles in place so the user never sees a
    // flash for a background sync. A failed first load leaves the flag unset,
    // so the next attempt shows loading again instead of a silent empty grid.
    if (!hasLoadedBoardsRef.current) setBoardsLoading(true);
    boardsStore
      .listBoardSummaries()
      .then((list) => {
        hasLoadedBoardsRef.current = true;
        setBoards((list || []).sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)));
      })
      .catch(() => {
        // A failed background refresh must not wipe tiles already on screen;
        // only a failed FIRST load shows the (honest) empty state.
        if (!hasLoadedBoardsRef.current) setBoards([]);
      })
      .finally(() => setBoardsLoading(false));
  }, []);

  // Reload the boards list whenever the pinned "Boards" tab becomes active
  // (covers first mount and switching back after editing boards elsewhere).
  const boardId = tab?.boardId || null;
  // "Last opened" for the overview (cards 7.1/7.3): local map, never synced.
  const markBoardOpened = useBoardsUiStore((s) => s.markBoardOpened);
  useEffect(() => {
    if (isActive && boardId) markBoardOpened(boardId);
  }, [isActive, boardId, markBoardOpened]);

  // Ctrl/Cmd+F focuses the boards filter, mirroring the "find" reflex instead
  // of opening the webview's native find bar. Moved here with the input it
  // focuses — it used to live in BoardsTitleBarSearch.
  const boardFilterRef = useRef(null);
  useEffect(() => {
    if (boardId) return undefined;
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && (e.key === 'f' || e.key === 'F')) {
        if (!boardFilterRef.current) return;
        e.preventDefault();
        boardFilterRef.current.focus();
        boardFilterRef.current.select();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [boardId]);

  // Tell the titlebar to show its "Filter boards" search only on the boards
  // home grid (no board open) once at least one board exists; hide it when a
  // board is open, the grid is empty, or this panel unmounts.
  const setSearchVisible = useTaskBoardUiStore((s) => s.setSearchVisible);
  useEffect(() => {
    // Always false: the search moved into the content area above the grid.
    // Left wired rather than deleted so the titlebar slot stays available, and
    // so nothing else that reads searchVisible starts rendering a second
    // search for the same value.
    setSearchVisible(false);
    return () => setSearchVisible(false);
  }, [boardId, boardsLoading, boards.length, setSearchVisible]);
  // Always-current boardId for async callbacks whose effect closure would
  // otherwise capture a stale value (the boardsVersion sync effect).
  boardIdRef.current = boardId;
  useEffect(() => {
    if (!tab || boardId) return;
    loadBoards();
  }, [tab, boardId, loadBoards]);

  // Re-list when team membership changes so team-scope boards appear/disappear.
  const teamIdsKey = useMemo(() => (teams || []).map((t) => t?.id).join(','), [teams]);
  useEffect(() => {
    if (boardId) return;
    loadBoards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamIdsKey]);

  // ---- Assignees: the team member list behind the picker and the card chips.
  // Fetched ONCE per board mount and cached per team in boardsUiStore — a
  // 40-card board must not make 40 requests, and every card reads the same
  // map. Private boards never fetch: they have no members to assign to.
  const boardTeamId = board?.visibility === 'team' && board?.teamId != null ? String(board.teamId) : null;
  const teamMembersByTeam = useBoardsUiStore((s) => s.teamMembersByTeam);
  const setTeamMembers = useBoardsUiStore((s) => s.setTeamMembers);
  useEffect(() => {
    if (!boardId || !boardTeamId) return;
    // Already cached from an earlier mount of this or another board on the
    // same team — the roster changes far more slowly than boards are opened.
    if (useBoardsUiStore.getState().teamMembersByTeam[boardTeamId]) return;
    let cancelled = false;
    teamsService
      .getTeamMembers(boardTeamId)
      .then((res) => {
        if (cancelled) return;
        setTeamMembers(boardTeamId, Array.isArray(res) ? res : res?.data || []);
      })
      .catch(() => {
        // A failed fetch leaves the cache empty: the picker shows only
        // "Unassigned" and chips fall back to "?" — assignment ids already on
        // cards are never lost, only their names.
      });
    return () => {
      cancelled = true;
    };
  }, [boardId, boardTeamId, setTeamMembers]);

  const boardMembers = useMemo(
    () => (boardTeamId ? teamMembersByTeam[boardTeamId] || [] : []),
    [boardTeamId, teamMembersByTeam]
  );
  const memberById = useMemo(() => {
    const m = new Map();
    for (const member of boardMembers) if (member?.id != null) m.set(String(member.id), member);
    return m;
  }, [boardMembers]);

  // A chat share card asked to open a specific task. The board tab is opened
  // first and this component mounts afterwards, so the request is parked in
  // the store and collected here. Cleared as soon as it is honoured — leaving
  // it set would reopen the modal every time the board re-renders, including
  // after the user closes it.
  const pendingTaskFocus = useBoardsUiStore((s) => s.pendingTaskFocus);
  const clearTaskFocus = useBoardsUiStore((s) => s.clearTaskFocus);
  useEffect(() => {
    if (!pendingTaskFocus || !boardId) return;
    if (String(pendingTaskFocus.boardId) !== String(boardId)) return;
    // Only once the board has actually loaded and contains the task: opening
    // the modal for an id the board does not have renders nothing and looks
    // like the click did not work.
    const exists = (boardRef.current?.tasks || []).some(
      (t) => String(t.id) === String(pendingTaskFocus.taskId)
    );
    if (!exists) return;
    setSelectedTaskId(pendingTaskFocus.taskId);
    clearTaskFocus();
    // `board` is a dependency on purpose: the request usually arrives in the
    // same moment the tab switches boards (Graph / Projects "Open"), before
    // that board has loaded, so it must be re-checked once the board lands.
  }, [pendingTaskFocus, boardId, clearTaskFocus, boardsVersion, board]);

  // Assigning straight from the card. Null on a personal board, which is what
  // turns the card avatar from a control back into a plain marker — there is
  // nobody else to assign to, so offering the menu would be offering an empty
  // list. Memoised because it is a prop on every card: a fresh object each
  // render would re-render the whole board on any state change.
  const [shareTaskTarget, setShareTaskTarget] = useState(null);
  const assignCtx = useMemo(
    () => (boardTeamId ? { members: boardMembers, currentUserId, onShare: setShareTaskTarget } : null),
    [boardTeamId, boardMembers, currentUserId]
  );

  // ---- Filter bar state. Purely local (see boardsUiStore): it never enters a
  // board's `data` and never reaches sync. Cleared whenever a different board
  // is opened — carrying "Mine" across boards would silently hide most of the
  // next board.
  const filters = useBoardsUiStore((s) => s.filters);
  const resetFilters = useBoardsUiStore((s) => s.resetFilters);
  useEffect(() => {
    resetFilters();
  }, [boardId, resetFilters]);
  // Memoised: it is a prop on every card AND on the filter bar, and paletteOf
  // builds a new array each call — an unmemoised one would re-render the whole
  // grid on any state change.
  const boardPalette = useMemo(() => paletteOf({ labels: board?.labels }), [board?.labels]);
  // What resolveLabels reads from a board — only its labels — kept stable so
  // the memoised cards do not re-render on every board change (big boards).
  const labelSource = useMemo(() => ({ labels: board?.labels }), [board?.labels]);
  // Task keys (commit ↔ task links): numbers filled in once for writers, and
  // one id → key map for the card, the list row and the task window.
  // `taken` stays null until the boards list has loaded once, so a new prefix
  // is never chosen blind to the other boards' prefixes.
  const boardsKnown = boards.length > 0 || hasLoadedBoardsRef.current;
  const takenPrefixes = useMemo(() => (boardsKnown ? prefixesTakenByOthers(boards, board?.id) : null), [boardsKnown, boards, board?.id]);
  // Kept identical while no key changed: a drag step or a title edit must not
  // hand every card and list a new filter (big boards re-render per frame).
  const taskKeysRef = useRef(null);
  const taskKeys = useMemo(() => {
    const next = board ? taskKeysFor(board, takenPrefixes || []) : null;
    const prev = taskKeysRef.current;
    if (prev && next && prev.size === next.size && [...next].every(([id, k]) => prev.get(id) === k)) return prev;
    taskKeysRef.current = next;
    return next;
  }, [board, takenPrefixes]);
  const boardKeyPrefix = board?.keyPrefix;
  const boardKeyAliases = board?.keyPrefixAliases;
  const keyAliases = useMemo(() => prefixAliasesOf({ keyPrefixAliases: boardKeyAliases }).filter((p) => p !== boardKeyPrefix), [boardKeyAliases, boardKeyPrefix]);
  const isFiltering = dragLocked(filters);
  // The search box finds a task by its key too (APB-42, or an old key of a renamed board).
  const filterCtx = useMemo(() => ({ currentUserId, keyOf: (t) => taskKeys?.get(String(t.id)) || null, keyAliases }), [currentUserId, taskKeys, keyAliases]);
  const taskVisible = useCallback(
    (t) => taskMatchesFilters(t, filters, filterCtx),
    [filters, filterCtx]
  );
  // Lists, their visible cards and progress — memoised with stable identities
  // so a drag step re-renders only the lists it touched (big boards).
  const boardGroups = board?.groups;
  const sortedGroups = useMemo(
    () => (boardGroups ? [...boardGroups].sort(boardsStore.compareGroupsByPosition) : []),
    [boardGroups]
  );
  const groupIds = useMemo(() => sortedGroups.map((g) => `group:${g.id}`), [sortedGroups]);
  // The pill's "done" filter shows done cards even on a board that hides them.
  const showDoneTasks = board ? board.showDoneTasks !== false || filters.state === 'done' : true;
  const kanbanVisible = useCallback((t) => (showDoneTasks || !t.done) && taskVisible(t), [showDoneTasks, taskVisible]);
  const groupTasks = useGroupTasks(board, sortedGroups, kanbanVisible, sortByPosition);
  // The filter bar's input: unchanged by a pure move, so it skips drag steps.
  const filterBarTasks = useMoveStableTasks(board?.tasks);

  const {
    scheduleDebounce,
    flushSave,
    flushIfTeamBoard,
  } = useBoardPersistence({ aliveRef, boardRef, burstStartRef, deletedIdsRef, dirtyEntityIdsRef, dirtyRef, persistRef, pullPendingRef, recomputeSaveState, retryTimeoutRef, saveTimeoutRef, savingRef });

  const { pushUndo, undo, redo, updateBoard } = useBoardDiskSync({
    boardsVersion, pendingBoardsBumpRef, boardId, setBoard, setBoardLoading, selectedTaskId,
    setSelectedTaskId, deleteGroupTarget, boardModal, deleteBoardTarget, readOnlyRef,
    setActiveDragId, scheduleDebounce, loadedBoardIdRef, boardIdRef, dragActiveRef, pullPendingRef,
    reconcileGenRef, pendingBoardChangesRef, applyBoardRecordsRef, reconcileFromDiskRef,
    boardCacheRef, dirtyRef, dirtyEntityIdsRef, markDirtyEntity, recomputeSaveState, flushSave,
    boardRef, undoStackRef, redoStackRef, syncHistoryFlags, resetHistory, loadBoards,
  });

  const {
    openCreateBoardModal, openEditBoardModal, requestDeleteFromModal, movingBoardId, cloudFull,
    setCloudFull, canMoveLocation, moveBoardLocation, submitBoardModal, confirmDeleteBoard,
  } = useBoardAdmin({ boardCacheRef, boardId, boardModal, boardRef, deleteBoardTarget, deletedIdsRef, dirtyEntityIdsRef, dirtyRef, loadBoards, loadedBoardIdRef, markDirtyEntity, persistRef, recomputeSaveState, setBoard, setBoardModal, setDeleteBoardTarget, shareableTeams });

  // Plans v2: tasks on the user's own boards (boards/ownTaskCount.js). The
  // open board is read when the + is pressed; the summaries may not be loaded.
  const ownTaskCount = useCallback(
    () =>
      countOwnTasks({
        open: boardRef.current,
        summaries: boards,
        summariesLoaded: hasLoadedBoardsRef.current,
        listSummaries: boardsStore.listBoardSummaries,
      }),
    [boards]
  );

  const {
    handleAddListInline, moveTaskToNewList, handleDeleteGroup, handleRenameGroup, handleAddTask,
    handleChangeTask, handleAddLabelToTask, handleMoveTaskToList, handleDeleteTask,
  } = useBoardTaskActions({ boardRef, boardTeamId, currentUserId, deleteGroupTarget, flushIfTeamBoard, justCreatedGroupIdsRef, justCreatedTaskIdsRef, markDirtyEntity, ownTaskCount, setDeleteGroupTarget, setSelectedTaskId, updateBoard });

  useBoardCreatedByFill({ board, readOnly: boardReadOnly, updateBoard });
  // Task keys: numbers filled in once for writers (the keys themselves are
  // computed above the filters, which search them).
  useBoardTaskKeysFill({ board, readOnly: boardReadOnly, updateBoard, taken: takenPrefixes });
  // "Fixes WEB-4" on the remote default branch closes WEB-4 (slice 3, board switch).
  useCommitAutoClose({ board, readOnly: boardReadOnly, updateBoard, taskKeys, keyAliases });

  const {
    handleDragOver, handleDragStart, handleDragEnd, handleDragCancel, collisionDetectionStrategy,
  } = useBoardDrag({ applyBoardRecordsRef, boardRef, dragActiveRef, dragUndoSnapshotRef, flushIfTeamBoard, markDirtyEntity, moveTaskToNewList, pendingBoardChangesRef, pendingBoardsBumpRef, pushUndo, reconcileFromDiskRef, setActiveDragId, syncHistoryFlags, updateBoard });

  // Cards skip painting off-screen (`content-visibility: auto`, TaskCard.css)
  // only while the open board is on screen and its layout has settled — never
  // across a hide/show or a zoom step, where the engine's stale decision left
  // the board blank (hooks/useSettledLayout.js).
  const cardsMayDefer = useSettledLayout(isActive && !!boardId && !!board && !boardLoading);

  if (!tab) {
    return <div className="task-board-empty">No board selected</div>;
  }

  return (
    <TaskKeysContext.Provider value={taskKeys}>
      <TaskKeyAliasesContext.Provider value={keyAliases}>
      {renderTaskBoardTab({
    board, boards, roleForTeam, boardFilter, boardId, isActive, cardsMayDefer, boardsLoading, setBoardFilter,
    shareableTeams, teamById, boardLoading, selectedTaskId, setSelectedTaskId, deleteGroupTarget,
    setDeleteGroupTarget, boardModal, setBoardModal, shareBoardTarget, setShareBoardTarget,
    privateBoardTarget, setPrivateBoardTarget, deleteBoardTarget, setDeleteBoardTarget, updateBoard,
    boardCloudReadOnly, boardReadOnly, currentUserId, boardViewers, activeDragId, redo, canUndo,
    canRedo, justCreatedTaskIdsRef, justCreatedGroupIdsRef, saveState, undo, sensors, loadBoards,
    boardFilterRef, boardTeamId, boardMembers, memberById, shareTaskTarget, setShareTaskTarget,
    assignCtx, boardPalette, labelSource, isFiltering, taskVisible, sortedGroups, groupIds,
    showDoneTasks, groupTasks, filterBarTasks, handleDragEnd, handleDragCancel,
    openCreateBoardModal, openEditBoardModal, requestDeleteFromModal, movingBoardId, cloudFull,
    setCloudFull, canMoveLocation, moveBoardLocation, submitBoardModal, confirmDeleteBoard,
    handleAddListInline, handleDeleteGroup, handleRenameGroup, handleAddTask, handleChangeTask,
    handleAddLabelToTask, handleMoveTaskToList, handleDeleteTask, handleDragOver, handleDragStart,
    collisionDetectionStrategy,
      })}
      </TaskKeyAliasesContext.Provider>
    </TaskKeysContext.Provider>
  );
}
